import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { apiCall } from '../api';

const Story = () => {
    // Component that renders a story 

    const storyId = useParams()['story-url'].split('-')[0]; // get the story id from the url

    const [storyTitle, setStoryTitle] = useState(''); // title of the story
    const [storyAuthor, setStoryAuthor] = useState(''); // author of the story

    // story state
    const [storyDict, setStoryDict] = useState({}); // the story dictionary - contains all the elements of the story
    const [history, setHistory] = useState([]); // the history of the story - all the elements that have been displayed
    const [currentElement, setCurrentElement] = useState({}); // the current element being displayed
    const [currentPlace, setCurrentPlace] = useState([]); // the current place in the story - [chapter, element]

    // help popup
    const [helpPopup, setHelpPopup] = useState(false); // if the help popup is open
    const [helpPopupFadeClass, setHelpPopupFadeClass] = useState('help-popup-in'); // class for fading the help popup

    // end of story
    const [storyEnd, setStoryEnd] = useState(false);

    // speed of the text animation
    const animationSpeed = 0.03;
    
    // need refs for these as they get updated from the event listener
    const storyDictRef = useRef(storyDict);
    const historyRef = useRef(history);
    const currentElementRef = useRef(currentElement);
    const currentPlaceRef = useRef(currentPlace);

    useEffect(() => { storyDictRef.current = storyDict; }, [storyDict]);
    useEffect(() => { historyRef.current = history; }, [history]);
    useEffect(() => { currentElementRef.current = currentElement; }, [currentElement]);
    useEffect(() => { currentPlaceRef.current = currentPlace; }, [currentPlace]);


    // upon first render, load the story
    useEffect(() => {

        const loadStory = async () => {

            // load the story info (title and author)
            const [infoSuccess, infoResp] = await apiCall({
                method: 'post',
                url: '/perilandintrigue/get_story_info/',
                data: {
                    story_id: storyId,
                },
            });

            setStoryTitle(infoResp.title);
            setStoryAuthor(infoResp.author);

            // load the story
            const [storySuccess, storyResp] = await apiCall({
                method: 'post',
                url: '/perilandintrigue/get_story/',
                data: {
                    story_id: storyId,
                },
            });

            if (!storySuccess || !infoSuccess) {
                alert('Failed to load story');
                return;
            };

            // set the story state
            setStoryDict(storyResp);
            storyDictRef.current = storyResp;

            setCurrentElement(storyResp['1']['1']);
            currentElementRef.current = storyResp['1']['1'];

            setCurrentPlace([1, 1]);
            currentPlaceRef.current = [1, 1];
        };

        loadStory();

    }, []);

    // event listeners for advancing the story
    useEffect(() => {
        window.addEventListener("keypress", handleKeyPress);
        window.addEventListener("click", handleKeyPress);
    
        return () => {
            // Clean up the event listener when the component is unmounted
            window.removeEventListener("keypress", handleKeyPress);
            window.removeEventListener("click", handleKeyPress);
        };
    }, []);
    
    // advances to the next element
    const advanceElement = (currentChapterNum, currentElementNum) => {
        // params: 
        // currentChapterNum: the current chapter number
        // currentElementNum: the current element number

        // update the history with the current element
        const newHistory = [...historyRef.current, currentElementRef.current];
        setHistory(newHistory);
        historyRef.current = newHistory;

        // check if the next element is a link
        let nextElement = storyDictRef.current[currentChapterNum][currentElementNum + 1];
        if (nextElement.type === 'Link') {
            // if it is, set the link to the current element
            setCurrentElement(nextElement);
            currentElementRef.current = nextElement;
            
            // then advance to the next chapter
            advanceChapter(nextElement.to_chapter);
            return;
        }

        // check if there's a next element in the current chapter
        if ((currentElementNum + 1) in storyDictRef.current[currentChapterNum]) {
            // if there is, advance to it
            setCurrentPlace([currentChapterNum, currentElementNum + 1]);
            currentPlaceRef.current = [currentChapterNum, currentElementNum + 1];
            setCurrentElement(storyDictRef.current[currentChapterNum][currentElementNum + 1]);
            currentElementRef.current = storyDictRef.current[currentChapterNum][currentElementNum + 1];
        } else {
            // if there's not, the story is over
            setStoryEnd(true);
        }
    };

    // advances to a new chapter
    const advanceChapter = (newChapterNum, decisionOptionNum = false, elementNum = false) => {
        // params:
        // newChapterNum: the new chapter number
        // decisionOptionNum: the decision option number - if it's a decision element || default: false
        // elementNum: the element number in the history - if it's not the current element || default: false

        // if the selected element is not the current element
        // we need to update the history to backtrack to the chosen decision
        // this allows the user to go back and choose a different option from a past decision
        // then play from there
        if (elementNum && (elementNum !== historyRef.current.length)) {

            // we need to update the current element
            let toElement = historyRef.current[elementNum];
            setCurrentElement(toElement);
            currentElementRef.current = toElement;

            // then we need to remove the future items from the history
            // this is because the user can go back to the decision and choose a different option
            let newHistory = [];            
            historyRef.current.map((element, num) => {
                if (num < elementNum) {
                    newHistory.push(element);
                }
            })

            // set this new history
            setHistory(newHistory);
            historyRef.current = newHistory;
        }

        // if it's a decision, we need to add the chosen option to the element
        if (currentElementRef.current.type === 'Decision') {

            // want to add which option was chosen
            let newElement = { ...currentElementRef.current };
            
            newElement['chosen_option'] = decisionOptionNum;

            setCurrentElement(newElement);
            currentElementRef.current = newElement;

        }

        // update the history
        const newHistory = [...historyRef.current, currentElementRef.current];
        setHistory(newHistory);
        historyRef.current = newHistory;

        // advance to the new chapter
        setCurrentPlace([newChapterNum, 1])
        currentPlaceRef.current = [newChapterNum, 1];
        setCurrentElement(storyDictRef.current[newChapterNum][1])
        currentElementRef.current = storyDictRef.current[newChapterNum][1];

    };

    // handle key press or click event
    const handleKeyPress = (e) => {
        // params:
        // e: the event

        try {
            // fade the help popup out
            setHelpPopupFadeClass('help-popup-out');
            // wait for the popup to fade out
            setTimeout(() => {
                setHelpPopup(false);
            }, 500);

            // if the current element is a decision, don't do anything
            if (currentElementRef.current.type == 'Decision') {
                return;
            }

            // otherwise, advance the story to the next element
            if (e.key === 'Enter' || e.type === 'click') {

                let currentChapterNum = currentPlaceRef.current[0];
                let currentElementNum = currentPlaceRef.current[1];

                // if it's a link, we need to advance the chapter
                if (currentElementRef.current.type === 'Link') {
                    advanceChapter(currentElementRef.current.to_chapter);
                }
                // if we're going to remain in the same chapter, just need to advance the element
                else if ((currentElementNum + 1) in storyDictRef.current[currentChapterNum]) {
                    advanceElement(currentChapterNum, currentElementNum);
                }
                // if we don't have a link and there's no next element in the chapter, the story is over
                else {
                    setStoryEnd(true);
                }

                // scroll the window down to the bottom
                window.scrollBy({ top: 1000000000000, behavior: 'instant' });
            }
        } catch (error) {
            console.log(error);
        }
    };

    // handle clicking on a decision option
    const handleDecisionOptionClick = (e, toChapter, optionNum, elementNum) => {
        // params:
        // e: the event
        // toChapter: the chapter number to go to
        // optionNum: the option number
        // elementNum: the element number in the history

        // prevent the event from bubbling up
        e.stopPropagation();

        // set the story end to false - this is if the player has reached the end,
        // and has now gone back to a decision to choose a different option
        setStoryEnd(false);
            
        // advance to the new chapter
        advanceChapter(toChapter, optionNum, elementNum);
    };

    // handle clicking on the author name
    const handleAuthorClick = (e) => {
        // params: 
        // e: the event

        // prevent the event from bubbling up
        e.stopPropagation();

        // go to the author's stories page
        window.location.href = `/stories/author/${storyAuthor}`;

    };

    // handle clicking on the help button
    const handleHelpClick = (e) => {
        // params:
        // e: the event

        // prevent the event from bubbling up
        e.stopPropagation();

        // if it's oopen, then fade it out
        if (helpPopup) {
            setHelpPopupFadeClass('help-popup-out');
            // wait for the popup to fade out
            setTimeout(() => {
                setHelpPopup(false);
            }, 500);
        }
        // if it's closed, then fade it in
        else {
            setHelpPopupFadeClass('help-popup-in');
            setHelpPopup(true);
        }


    };

    // formats the text of an element - so it's ready to render
    const formatElementText = (element) => {
        // params:
        // element: the element to format

        let text = [];

        // narratives get displayed as normal
        if (element.type === 'Narrative') {
            text.push([element.text, 'normal']);
        }
        // dialog gets displayed with the character name bolded
        else if (element.type === 'Dialog') {
            text.push([`${element.character}:  `, 'bold']);
            text.push([`"${element.text}"`, 'normal']);
        }
        // thoughts get displayed with the character name bolded and the thought italicized
        else if (element.type === 'Thought') {
            text.push([`${element.character}: `, 'bold']);
            text.push([element.text, 'italic']);
        }
        // decisions get displayed with the question italicized, and the options displayed as normal
        else if (element.type === 'Decision') {
            text.push([element.text, 'italic']);
            // the options need some additional information
            //// the option number, and the chapter they go to
            Object.keys(element.options).forEach((option) => {
                text.push([element.options[option].text, 'normal', option, element.options[option].to_chapter]);
            });
        }

        return text;
    };

    // renders a line of text
    const renderText = (words, textStyle, delay, elementNum, textIndex, fadeElement = false) => {
        // params: 
        // words: the words to display
        // textStyle: the style of the text
        // delay: the delay before displaying the first word
        // elementNum: the number of the element in history
        // textIndex: the index of the text in the element
        // fadeElement: if the element is the current element aka whether it should be faded in || default: false

        return (
            words.split(' ').map((word, wordIndex) => {
                delay += animationSpeed;

                return (
                    <span
                        className={fadeElement ? 'fade-word' : ''}
                        key={`element-${elementNum}-${textIndex}-${wordIndex}`} // Ensure unique key for each span
                        style={{ 
                            animationDelay: fadeElement ? `${delay}s` : 'none',
                            fontStyle: textStyle,
                            fontWeight: textStyle
                        }}
                    >
                        {`${word} `}
                    </span>
                );
            })
        );
    };

    // renders a decision option 
    const renderDecisionOption = (words, textStyle, toChapter, optionNum, chosenOption,
        delay, elementNum, textIndex, fadeElement = false) => {
        // params:
        // words: the words to display
        // textStyle: the style of the text
        // toChapter: the chapter to go to
        // optionNum: the option number
        // chosenOption: the chosen option
        // delay: the delay before displaying the first word
        // elementNum: the number of the element in history
        // textIndex: the index of the text in the element
        // fadeElement: if the element is the current element aka whether it should be faded in || default: false

        // check if the option is the chosen option
        let chosen = chosenOption === optionNum;

        return (
            <div 
                id={`element-${elementNum}-${textIndex}`}
                className={chosen ? 'decision-option chosen' : 'decision-option'}
                style={{
                    animation: !fadeElement ? 'none' : null,
                    opacity: !fadeElement ? 1 : null,
                    animationDelay: `${delay}s`,
                }}
                onClick={(e) => handleDecisionOptionClick(e, toChapter, optionNum, elementNum)}
            >
                {words.split(' ').map((word, wordIndex) => {
                    delay += animationSpeed;

                    return (
                        <span
                            id={`element-${elementNum}-${textIndex}-${wordIndex}`}
                            className={fadeElement ? 'fade-word' : ''}
                            key={`element-${elementNum}-${textIndex}-${wordIndex}`} // Ensure unique key for each span
                            style={{ 
                                animationDelay: fadeElement ? `${delay}s` : 'none',
                                fontStyle: textStyle,
                                fontWeight: textStyle
                            }}
                        >
                            {`${word} `}
                        </span>
                    );
                })}
            </div>
        );
    };

    // renders the elements of the story - the history, plus the current element
    const renderElements = () => {
        return (
            <div className='story-elements-container'>
                {/* render the history */}
                {historyRef.current.map((element, elementIndex) => {
                    return (
                        renderElement(element, elementIndex, false)
                    );
                })}

                {/* render the current element */}
                {renderElement(currentElementRef.current, historyRef.current.length, true)}
            </div>
        );
    };

    // renders a story element
    const renderElement = (element, elementNum, fadeElement = false) => {
        // if it's a link, then just render a blank space
        if (element.type === 'Link') {
            return (
                <div className='link' />
            );
        }

        // get the text array from the element
        let textArr = formatElementText(element);

        // have a very slight delay before it begins displaying text
        let currentDelay = 0.1;

        // rendering a decision element
        if (element.type === 'Decision') {
            // get the delay for the decision options
            // this is based off how long the question is
            let decisionOptionDelay = (textArr[0][0].split(' ').length * animationSpeed) + 0.5;

            // get the chosen decision option
            let chosenOption = fadeElement ? null : element['chosen_option'];

            return (
                <div 
                    key={`element-${elementNum}`}
                    className='decision text paragraphs'
                >
                    {textArr.map((item, textIndex) => {
                        let words = item[0];
                        let textStyle = item[1];

                        // first item will be the decision question
                        if (textIndex === 0) {
                            return (
                                <div style={{ marginBottom: '10px' }}>
                                    {renderText(words, textStyle, currentDelay, elementNum, textIndex, fadeElement)}
                                </div>
                            );
                        } 
                        // remaining items will be decision options
                        else {
                            let optionNum = item[2];
                            let toChapter = item[3];
                            return (
                                renderDecisionOption(words, textStyle, toChapter, optionNum, chosenOption,
                                    decisionOptionDelay, elementNum, textIndex, fadeElement)
                            );
                        }
                    })}
                </div>
            );
        } 
        // rendering narrative, dialog, or thought elements
        else {
            return (
                <div 
                    key={`element-${elementNum}`}
                    className='text paragraphs'
                >
                    {textArr.map((item, textIndex) => {
                        let words = item[0];
                        let textStyle = item[1];

                        return (
                            renderText(words, textStyle, currentDelay, elementNum, textIndex, fadeElement)
                        );
                    })}
                </div>
            );
        }
    };

    
    return (
        <div 
            className='container story-container flex-column' 
            style={{ alignItems: 'start' }}
            onKeyDown={(e) => handleKeyPress(e)}
        >
            
            {/* render the title and author */}
            <h1 
                className='text title'
                style={{ marginBottom: '1%' }}
            >
                {storyTitle}
            </h1>
            
            <div className='title-underline'/>
            
            <h1 className='text author'>
                by&nbsp;
                <span
                    style={{ cursor: 'pointer', fontStyle: 'italic' }}
                    onClick={(e) => handleAuthorClick(e)}
                >
                    {storyAuthor}
                </span>
            </h1>
            
            <div 
                className='title-underline' 
                style={{ marginBottom: '5%' }}
            />

            {/* render the story elements */}
            { renderElements() }

            {/* render the end of the story */}
            {storyEnd && 
                <div
                    className='container flex-column'
                    style={{ width: '100%' }}
                >
                    <div 
                        className='title-underline end-story-underline' 
                        style={{ marginTop: '5%' }}
                    />
                    <div className='text paragraphs'>
                        The End 
                    </div>
                </div>
            }

            {/* render a spacer below the story */}
            <div style={{ minHeight: '300px' }} />

            {/* render the help button */}
            <div 
                className='help-button text help'
                onClick={(e) => handleHelpClick(e)}
            >
                ?
            </div>

            {/* render the help popup */}
            {helpPopup &&
                <div 
                    className={ `help-popup ${helpPopupFadeClass}` }
                    onClick={(e) => e.stopPropagation()}
                >
                    <div 
                        className='container flex-column text paragraphs'
                        style={{ gap: '10px' }}
                    >
                        <div style={{ fontStyle: 'italic' }}>Help</div>
                        <div style={{ textAlign: 'center' }}>
                            Click or press enter to advance the story.
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            You can go back to any decision and choose a new option - and the story will play from that point.
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default Story;
